/**
 * Tanks Page
 *
 * Provides tank management interface:
 * - The user's tanks as cards with their cover photo and tank shot count
 * - Create new tank
 * - Edit tank (name, description)
 * - Delete tank with confirmation
 * - Shows selected tank indicator
 */

import React, { useState } from 'react';
import {
  Container,
  Title,
  Stack,
  Paper,
  SimpleGrid,
  Button,
  Group,
  Text,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  Alert,
  Checkbox,
  Tooltip,
} from '@mantine/core';
import { IconPlus, IconAlertCircle } from '@tabler/icons-react';
import { useTank } from '../contexts/TankContext';
import { useAuth } from '../contexts/AuthContext';
import { Tank, CreateTankData } from '../types/tank';
import type { VolumeUnit } from '../types/auth';
import { api } from '../api/client';
import { TankCard } from '../features/tanks/TankCard';
import { useRefetchOnImageError } from '../hooks/usePhotoInbox';

const VOLUME_UNIT_OPTIONS = [
  { value: 'litres', label: 'Litres' },
  { value: 'gallons', label: 'Gallons' },
];

export const TanksPage: React.FC = () => {
  const { tanks, selectedTank, selectTank, refreshTanks } = useTank();
  const { user } = useAuth();
  // An expired signed cover URL: a fresh tanks list signs new ones (once for all the
  // cards that fail together, and not again straight away if the new ones fail too).
  const onCoverError = useRefetchOnImageError(refreshTanks);

  // Modal states
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState<Tank | null>(null);
  const [selectedForDelete, setSelectedForDelete] = useState<Tank | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [volume, setVolume] = useState<number | string>('');
  const [volumeUnit, setVolumeUnit] = useState<VolumeUnit>('litres');
  const [isDefault, setIsDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form
  const resetForm = () => {
    setName('');
    setDescription('');
    setVolume('');
    setVolumeUnit(user?.volume_unit || 'litres');
    setIsDefault(false);
    setError(null);
  };

  // Open create modal
  const handleCreate = () => {
    resetForm();
    setCreateModalOpened(true);
  };

  // Open edit modal
  const handleEdit = (tank: Tank) => {
    setSelectedForEdit(tank);
    setName(tank.name);
    setDescription(tank.description || '');
    setVolume(tank.volume ?? '');
    setVolumeUnit(tank.volume_unit || user?.volume_unit || 'litres');
    setIsDefault(tank.is_default || false);
    setError(null);
    setEditModalOpened(true);
  };

  // Open delete modal
  const handleDelete = (tank: Tank) => {
    setSelectedForDelete(tank);
    setDeleteModalOpened(true);
  };

  // Submit create form
  const handleSubmitCreate = async () => {
    if (!name.trim()) {
      setError('Tank name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const data: CreateTankData = {
        name: name.trim(),
        description: description.trim() || undefined,
        volume: typeof volume === 'number' ? volume : undefined,
        volume_unit: typeof volume === 'number' ? volumeUnit : undefined,
      };

      await api.post('/api/tanks', data);
      await refreshTanks();
      setCreateModalOpened(false);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tank');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit edit form
  const handleSubmitEdit = async () => {
    if (!selectedForEdit) return;
    if (!name.trim()) {
      setError('Tank name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const data = {
        name: name.trim(),
        description: description.trim() || null,
        is_default: isDefault,
        volume: typeof volume === 'number' ? volume : undefined,
        volume_unit: typeof volume === 'number' ? volumeUnit : undefined,
      };

      await api.put(`/api/tanks/${selectedForEdit.id}`, data);
      await refreshTanks();
      setEditModalOpened(false);
      setSelectedForEdit(null);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tank');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit delete
  const handleSubmitDelete = async () => {
    if (!selectedForDelete) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await api.delete(`/api/tanks/${selectedForDelete.id}`);
      await refreshTanks();
      setDeleteModalOpened(false);
      setSelectedForDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tank');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>My Tanks</Title>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            Create Tank
          </Button>
        </Group>

        {tanks.length === 0 ? (
          <Paper p="xl" withBorder>
            <Stack align="center" gap="md">
              <Text c="dimmed">You don't have any tanks yet.</Text>
              <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
                Create Your First Tank
              </Button>
            </Stack>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing={{ base: 'sm', md: 'md' }}>
            {tanks.map((tank) => (
              <TankCard
                key={tank.id}
                tank={tank}
                selected={tank.id === selectedTank?.id}
                onSelect={(t) => selectTank(t.id)}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onImageError={onCoverError}
              />
            ))}
          </SimpleGrid>
        )}

        {/* Create Modal */}
        <Modal
          opened={createModalOpened}
          onClose={() => setCreateModalOpened(false)}
          title="Create Tank"
          size="md"
        >
          <Stack gap="md">
            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                onClose={() => setError(null)}
                withCloseButton
              >
                {error}
              </Alert>
            )}

            <TextInput
              label="Tank Name"
              placeholder="My Reef Tank"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
            />

            <Textarea
              label="Description"
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              rows={3}
            />

            <Group grow>
              <NumberInput
                label="Tank Volume"
                placeholder="e.g. 100"
                min={0}
                decimalScale={1}
                value={volume}
                onChange={setVolume}
                disabled={isSubmitting}
              />
              <Select
                label="Unit"
                data={VOLUME_UNIT_OPTIONS}
                value={volumeUnit}
                onChange={(val) => setVolumeUnit((val as VolumeUnit) || 'litres')}
                disabled={isSubmitting}
              />
            </Group>

            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                onClick={() => setCreateModalOpened(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmitCreate} loading={isSubmitting}>
                Create
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Edit Modal */}
        <Modal
          opened={editModalOpened}
          onClose={() => setEditModalOpened(false)}
          title="Edit Tank"
          size="md"
        >
          <Stack gap="md">
            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                onClose={() => setError(null)}
                withCloseButton
              >
                {error}
              </Alert>
            )}

            <TextInput
              label="Tank Name"
              placeholder="My Reef Tank"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
            />

            <Textarea
              label="Description"
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              rows={3}
            />

            <Group grow>
              <NumberInput
                label="Tank Volume"
                placeholder="e.g. 100"
                min={0}
                decimalScale={1}
                value={volume}
                onChange={setVolume}
                disabled={isSubmitting}
              />
              <Select
                label="Unit"
                data={VOLUME_UNIT_OPTIONS}
                value={volumeUnit}
                onChange={(val) => setVolumeUnit((val as VolumeUnit) || 'litres')}
                disabled={isSubmitting}
              />
            </Group>

            <Tooltip
              label="This tank will be loaded automatically when you log in"
              position="top"
              withArrow
            >
              <Checkbox
                label="Set as default tank"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.currentTarget.checked)}
                disabled={isSubmitting}
              />
            </Tooltip>

            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                onClick={() => setEditModalOpened(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmitEdit} loading={isSubmitting}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={() => setDeleteModalOpened(false)}
          title="Delete Tank"
          size="md"
        >
          <Stack gap="md">
            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                onClose={() => setError(null)}
                withCloseButton
              >
                {error}
              </Alert>
            )}

            <Text>
              Are you sure you want to delete{' '}
              <Text component="span" fw={500}>
                {selectedForDelete?.name}
              </Text>
              ?
            </Text>

            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              This action cannot be undone. All data associated with this tank will be
              removed.
            </Alert>

            <Group justify="flex-end" gap="xs">
              <Button
                variant="subtle"
                onClick={() => setDeleteModalOpened(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button color="red" onClick={handleSubmitDelete} loading={isSubmitting}>
                Delete Tank
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
};
